import { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardHeader,
  Table,
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { toast } from "react-toastify";
import { URL } from "../../api";
import axiosInstance from "../../Request";

const Template = () => {
  const [formData, setFormData] = useState({ search: "" });
  const [statusFilter, setStatusFilter] = useState("");
  const [templateList, setTemplateList] = useState([]);
  const [filteredTemplateList, setFilteredTemplateList] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [addForm, setAddForm] = useState({
    type: "",
    subType: "",
    name: "",
    subject: "",
    body: "",
    templateId: "",
    userId: "",
    isActive: true,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    handleFilter();
  }, [formData.search, statusFilter, templateList]);

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get(URL.TEMPLATE_LIST);
      const data = response.data;
      setTemplateList(data);
      setFilteredTemplateList(data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFilter = () => {
    let filtered = templateList;

    if (formData.search) {
      filtered = filtered.filter((template) =>
        template.name?.toLowerCase().includes(formData.search.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((template) =>
        statusFilter === "active" ? template.isActive : !template.isActive
      );
    }

    setFilteredTemplateList(filtered);
  };

  const toggleModal = () => setShowModal(!showModal);

  const handleAddInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "type") {
      setAddForm((prev) => ({
        ...prev,
        type: value,
        subType: "",
        name: "",
        subject: "",
        body: "",
        templateId: "",
      }));
    } else {
      setAddForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleAddSubmit = async () => {
    if (!addForm.name || !addForm.type) {
      toast.error("Name and Type are required.");
      return;
    }

    const payload = {
      type: addForm.type,
      name: addForm.name,
      subject: addForm.subject || null,
      body: addForm.body || null,
      isActive: addForm.isActive,
      userId: addForm.userId,
      templateId: addForm.templateId || null,
      subType: addForm.subType || null,
    };

    try {
      await axiosInstance.post(URL.TEMPLATE_ADD, payload);
      toast.success("Template added successfully");
      await fetchTemplates();
      toggleModal();
      setAddForm({
        type: "",
        subType: "",
        name: "",
        subject: "",
        body: "",
        templateId: "",
        userId: "",
        isActive: true,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data || "Failed to add template. Please try again.";
      if (typeof errorMessage === "string") {
        toast.error(errorMessage);
      } else {
        toast.error("An unexpected error occurred.");
      }
    }
  };

  // ✅ Updated to use `name` instead of `templateId`
  const handleStatusChange = async (name, newStatus) => {
    try {
      await axiosInstance.post(URL.TEMPLATE_UPDATE, null, {
        params: {
          name: name,
          status: newStatus,
        },
      });
      toast.success("Status updated!");
      fetchTemplates();
    } catch (error) {
      toast.error("Error updating status.");
      console.error(error);
    }
  };

  return (
    <>
      <div className="header bg-gradient-info pb-6 pt-4 pt-md-7">
        <Container fluid>
          <Row>
            <Col>
              <h1 className="text-white">Name Templates</h1>
            </Col>
            <Col className="text-right">
              <Button color="success" onClick={toggleModal}>
                Add Template
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col xl="12">
            <Card className="shadow">
              <Row className="p-4">
                <Col xl="3">
                  <Input
                    type="text"
                    name="search"
                    value={formData.search}
                    onChange={handleChange}
                    placeholder="Search by Template Name"
                  />
                </Col>
                <Col xl="3">
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Input>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">Template List</h3>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Subject</th>
                    <th>Body</th>
                    <th>Template ID</th>
                    <th>User ID</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplateList.map((template) => (
                    <tr key={template.id}>
                      <td>{template.name}</td>
                      <td>{template.type}</td>
                      <td>
                        <Input
                          type="select"
                          value={template.isActive ? "1" : "0"}
                          onChange={(e) =>
                            handleStatusChange(template.name, e.target.value)
                          }
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </Input>
                      </td>
                      <td>{template.subject || "-"}</td>
                      <td>{template.body || "-"}</td>
                      <td>{template.templateId || "-"}</td>
                      <td>{template.userId}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Template Modal */}
      <Modal isOpen={showModal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Add Template</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Send via</Label>
              <Input
                type="select"
                name="type"
                value={addForm.type}
                onChange={handleAddInputChange}
              >
                <option value="">-- TYPE --</option>
                <option value="sms">sms</option>
                <option value="email">email</option>
              </Input>
            </FormGroup>

            {addForm.type === "sms" && (
              <>
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    name="name"
                    value={addForm.name}
                    onChange={handleAddInputChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Template ID (Optional)</Label>
                  <Input
                    type="text"
                    name="templateId"
                    value={addForm.templateId}
                    onChange={handleAddInputChange}
                  />
                </FormGroup>
              </>
            )}

            {addForm.type === "email" && (
              <>
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    name="name"
                    value={addForm.name}
                    onChange={handleAddInputChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Subject</Label>
                  <Input
                    type="text"
                    name="subject"
                    value={addForm.subject}
                    onChange={handleAddInputChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Body</Label>
                  <Input
                    type="textarea"
                    name="body"
                    value={addForm.body}
                    onChange={handleAddInputChange}
                  />
                </FormGroup>
              </>
            )}

            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="isActive"
                  checked={addForm.isActive}
                  onChange={handleAddInputChange}
                />{" "}
                Active
              </Label>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddSubmit}>
            Save
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Template;
